import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import api from './utils/api';

// Components
import Navbar from './components/Navbar';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import ParentDashboard from './components/ParentDashboard';
import AdminDashboard from './components/AdminDashboard';
import ForgotPassword from './components/ForgotPassword';
import ResetPasswordVerify from './components/ResetPasswordVerify';
import ResetPassword from './components/ResetPassword';
import PaymentRedirect from './components/PaymentRedirect';
import HomePage from './components/HomePage';


// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Set up axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token is still valid by making a test API call
      api.get('/api/health')
        .then(() => {
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          setUser(userData);
        })
        .catch(() => {
          // Token is invalid, remove it
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (userData, token) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', token);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  if (loading) {
    return (
      <div className="loading">
        <h2>🏏 {process.env.REACT_APP_ACADEMY_NAME || 'MSKICA Cricket Academy'}</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      <Router>
        <div className="App">
          <Navbar />
          <div className="container">
            <Routes>
              <Route 
                path="/login" 
                element={user ? <Navigate to="/dashboard" /> : <Login />} 
              />
              <Route 
                path="/register" 
                element={user ? <Navigate to="/dashboard" /> : <Register />} 
              />
              <Route 
                path="/verify-email" 
                element={user ? <Navigate to="/dashboard" /> : <VerifyEmail />} 
              />
              <Route 
                path="/forgot-password" 
                element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} 
              />
              <Route 
                path="/reset-password-verify" 
                element={user ? <Navigate to="/dashboard" /> : <ResetPasswordVerify />} 
              />
              <Route 
                path="/reset-password" 
                element={user ? <Navigate to="/dashboard" /> : <ResetPassword />} 
              />
              <Route 
                path="/payment/success" 
                element={user ? <PaymentRedirect type="success" /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/payment/cancel" 
                element={user ? <PaymentRedirect type="cancel" /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/payment/redirect" 
                element={user ? <PaymentRedirect type="redirect" /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/dashboard" 
                element={
                  user ? (
                    user.user_type === 'admin' || user.user_type === 'coach' ? 
                      <AdminDashboard /> : 
                      <ParentDashboard />
                  ) : <Navigate to="/login" />
                } 
              />

              <Route 
                path="/" 
                element={
                  user ? <Navigate to="/dashboard" /> : <HomePage />
                } 
              />
            </Routes>
          </div>
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
